import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

export default function AvailableRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setError(false);
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/creator-claims/available?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data.filter(req => req.count > 0));
    } catch (err) {
      console.error("Failed to fetch requests:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (verse, count) => {
    setClaiming(verse);
    setRequests(prev => prev.filter(req => req.verse !== verse));

    try {
      const response = await axios.post(`${API_URL}/creator-claims/claim`, { verse }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const claimId = response.data.claimId;
      alert(`Claimed ${verse} (${count} request${count > 1 ? 's' : ''})`);
      await fetchRequests();
      
      // ✅ Pass verse and claimId to the submit page
      navigate("/submit", { state: { verse, claimId } });
    } catch (err) {
      console.error("Claim error:", err);
      await fetchRequests();
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      alert(err.response?.data?.error || "Failed to claim. Try again.");
    } finally {
      setClaiming(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading requests...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <p className="text-gray-600 mb-4">Failed to load requests.</p>
        <button onClick={fetchRequests} className="bg-primary text-white px-4 py-2 rounded-md">Retry</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-primary mb-2">Available Requests</h1>
        <p className="text-gray-500 mb-6">Verses that users want explained. Claim one to get started.</p>

        {requests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No pending requests. Check back later!</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map(req => (
              <div key={req.verse} className="bg-white rounded-lg shadow p-6 text-center transition hover:shadow-md">
                <div className="text-xl font-bold text-primary mb-2">{req.verse}</div>

                {req.description && (
                  <div className="text-sm text-gray-600 mt-2 mb-3 italic bg-gray-50 p-2 rounded-md">
                    <span className="font-semibold">User confusion:</span>
                    <p className="mt-1">“{req.description}”</p>
                    {req.allContexts && req.allContexts.length > 1 && (
                      <details className="mt-1 text-left">
                        <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600">
                          + {req.allContexts.length - 1} more similar request{req.allContexts.length - 1 > 1 ? 's' : ''}
                        </summary>
                        <ul className="list-disc list-inside mt-1 text-gray-500 text-xs">
                          {req.allContexts.slice(1).map((ctx, idx) => (
                            <li key={idx} className="italic">“{ctx}”</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                <div className="text-gray-500 mb-4">{req.count} user{req.count > 1 ? 's' : ''} waiting</div>
                <button
                  onClick={() => handleClaim(req.verse, req.count)}
                  disabled={claiming === req.verse}
                  className="w-full bg-primary text-white py-2 rounded-md font-semibold hover:bg-opacity-90 transition disabled:opacity-50"
                >
                  {claiming === req.verse ? "Claiming..." : "Claim & Create Video"}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}